import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';

import ProductModel from 'coreClasses/product/ProductModel';
import VariantModel from 'coreClasses/product/VariantModel';
import {useProduct, useProducts, useSetUI, useUI} from 'coreHooks';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Flyout from 'brandComponents/Flyout';
import Picture from 'brandComponents/Picture';
import {PRODUCT_IMAGES_SETTINGS, ViewType} from 'brandConstants';

import messages from './messages';

import styles from './ColorSelectorFlyout.module.scss';

const ColorSelectorFlyout = () => {
	const {managerRepository} = useGeneralProvider();
	const [productWithError, setProductWithError] = useState('');
	const isServerUI = useUI('isServer');
	const intl = useIntl();
	const setUI = useSetUI();
	const colorSelectorUI = useUI('colorSelector');
	const productModel = useProduct(colorSelectorUI?.productID);
	const colorsIdList = useMemo(() => productModel.getVariationGroupIds(), [productModel.getProductId()]);
	const variantColors = useProducts(colorsIdList);

	const handleClose = () => {
		setUI.hideColorSelector();
	};

	const handleSelectColor = async ({isOrderable, productId}) => {
		setProductWithError('');

		if (isOrderable) {
			managerRepository.getUiMgr().setColorSelectorSelectedPid(productId);
			setUI.hideColorSelector();
		} else {
			setProductWithError(productId);
		}
	};

	useEffect(() => {
		if (colorSelectorUI?.productID) {
			managerRepository.getFlyoutsMgr().openFlyout('colorSelector', true);
		} else {
			managerRepository.getFlyoutsMgr().closeFlyout('colorSelector');
		}
	}, [colorSelectorUI?.productID]);

	return (
		<Flyout
			id="colorSelector"
			className={styles['color-selector-flyout']}
			onClose={handleClose}
			contentClassName={styles['color-selector__content']}
			closeClassName={styles['color-selector__close']}
			parentSelector={colorSelectorUI?.parentSelector}
			showCloseButton
			softLock
		>
			<div className={styles['color-selector-flyout__heading']}>
				<div className={styles['color-selector-flyout__heading--title-wrapper']}>
					<span>{intl.formatMessage(messages.selectColorHeading)}</span>
				</div>

				<div className={styles['color-selector-flyout__content']}>
					{variantColors.map((variantColor: ProductModel | VariantModel) => {
						const image = variantColor.getProductImage({
							viewType: ViewType.ZOOM,
							transformationObject: PRODUCT_IMAGES_SETTINGS.variantColor,
						});
						const availableColorId = variantColor.getProductId();

						return (
							<div key={availableColorId}>
								<Button
									variant="tertiary"
									classNames={styles['color-selector-flyout__button']}
									onClick={() =>
										handleSelectColor({
											isOrderable: variantColor.isOrderable(),
											productId: availableColorId,
										})
									}
								>
									<Picture image={image} showSkeleton={!isServerUI} />
								</Button>
								{productWithError === availableColorId && (
									<span className={styles['color-selector-flyout__error-message']}>{intl.formatMessage(messages.tileError)}</span>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</Flyout>
	);
};

export default ColorSelectorFlyout;
